import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit, Trash2, Search, Upload, Download, 
  Image, Link as LinkIcon, Code, Save, X, Check,
  Layout, Monitor, Smartphone, Tablet, ArrowRight,
  AlertCircle, Info, ExternalLink, Eye, EyeOff,
  Filter, ChevronDown, MoreHorizontal
} from 'lucide-react';

interface Advertisement {
  id: string;
  name: string;
  type: 'image' | 'code';
  location: 'masthead' | 'sidebar-left' | 'sidebar-right';
  pages: string[];
  imageUrl?: string;
  linkUrl?: string;
  htmlCode?: string;
  isActive: boolean;
  startDate: string;
  endDate: string;
  dimensions: string;
  viewCount: number;
  clickCount: number;
  createdAt: string;
  updatedAt: string;
}

const initialAds: Advertisement[] = [
  {
    id: '1',
    name: 'Kredi Kampanyası Banner',
    type: 'image',
    location: 'masthead',
    pages: ['ihtiyac-kredisi', 'konut-kredisi', 'tasit-kredisi'],
    imageUrl: 'https://images.unsplash.com/photo-1579621970795-87facc2f976d?auto=format&fit=crop&q=80&w=1200&h=200',
    linkUrl: 'https://example.com/kampanya',
    isActive: true,
    startDate: '2024-01-01',
    endDate: '2024-12-31',
    dimensions: '1200x200',
    viewCount: 1250,
    clickCount: 85,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-18T10:00:00Z'
  },
  {
    id: '2',
    name: 'Mevduat Yan Banner',
    type: 'image',
    location: 'sidebar-right',
    pages: ['mevduat'],
    imageUrl: 'https://images.unsplash.com/photo-1565514020179-026b92b2d70b?auto=format&fit=crop&q=80&w=300&h=600',
    linkUrl: 'https://example.com/mevduat',
    isActive: true,
    startDate: '2024-01-01',
    endDate: '2024-12-31',
    dimensions: '300x600',
    viewCount: 980,
    clickCount: 42,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-15T14:30:00Z'
  },
  {
    id: '3',
    name: 'Kredi Kartı JS Kodu',
    type: 'code',
    location: 'sidebar-left',
    pages: ['kredi-kartlari'],
    htmlCode: '<div style="width:300px;height:600px;background:#f0f0f0;display:flex;align-items:center;justify-content:center;border:1px solid #ddd;"><p style="text-align:center;padding:20px;">Kredi Kartı Reklam Alanı<br>(300x600)</p></div>',
    isActive: false,
    startDate: '2024-01-01',
    endDate: '2024-12-31',
    dimensions: '300x600',
    viewCount: 0,
    clickCount: 0,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-10T09:20:00Z'
  }
];

const pageOptions = [
  { value: 'home', label: 'Ana Sayfa' },
  { value: 'ihtiyac-kredisi', label: 'İhtiyaç Kredisi' },
  { value: 'konut-kredisi', label: 'Konut Kredisi' },
  { value: 'tasit-kredisi', label: 'Taşıt Kredisi' },
  { value: 'kobi-kredisi', label: 'Kobi Kredisi' },
  { value: 'kredi-kartlari', label: 'Kredi Kartları' },
  { value: 'mevduat', label: 'Mevduat' },
  { value: 'uzaktan-musteri-edinimi', label: 'Uzaktan Müşteri Edinimi' }
];

const locationOptions = [
  { value: 'masthead', label: 'Masthead (Üst Banner)', dimensions: '1200x200' },
  { value: 'sidebar-left', label: 'Sol Kenar', dimensions: '300x600' },
  { value: 'sidebar-right', label: 'Sağ Kenar', dimensions: '300x600' }
];

const AdvertisementManagement = () => {
  const [advertisements, setAdvertisements] = useState<Advertisement[]>(initialAds);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterPage, setFilterPage] = useState('');
  const [filterType, setFilterType] = useState('');
  const [editingAd, setEditingAd] = useState<Advertisement | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<Advertisement>>({});
  const [previewMode, setPreviewMode] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isCodePreviewOpen, setIsCodePreviewOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  const filteredAds = advertisements.filter(ad => {
    const matchesSearch = ad.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLocation = !filterLocation || ad.location === filterLocation;
    const matchesPage = !filterPage || ad.pages.includes(filterPage);
    const matchesType = !filterType || ad.type === filterType;
    
    return matchesSearch && matchesLocation && matchesPage && matchesType;
  });

  const handleEdit = (ad: Advertisement) => {
    setEditingAd(ad);
    setFormData(ad);
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setEditingAd(null);
    setFormData({
      name: '',
      type: 'image',
      location: 'masthead',
      pages: [],
      imageUrl: '',
      linkUrl: '',
      htmlCode: '',
      isActive: true,
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().split('T')[0],
      dimensions: '1200x200'
    });
    setIsModalOpen(true);
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name?.trim()) {
      newErrors.name = 'Reklam adı gereklidir';
    }
    
    if (!formData.pages || formData.pages.length === 0) {
      newErrors.pages = 'En az bir sayfa seçilmelidir';
    }
    
    if (formData.type === 'image') {
      if (!formData.imageUrl?.trim()) {
        newErrors.imageUrl = 'Görsel URL gereklidir';
      }
      
      if (!formData.linkUrl?.trim()) {
        newErrors.linkUrl = 'Yönlendirme linki gereklidir';
      }
    } else if (formData.type === 'code') {
      if (!formData.htmlCode?.trim()) {
        newErrors.htmlCode = 'HTML/JS kodu gereklidir';
      }
    }
    
    if (!formData.startDate) {
      newErrors.startDate = 'Başlangıç tarihi gereklidir';
    }
    
    if (!formData.endDate) {
      newErrors.endDate = 'Bitiş tarihi gereklidir';
    } else if (formData.startDate && new Date(formData.startDate) > new Date(formData.endDate)) {
      newErrors.endDate = 'Bitiş tarihi başlangıç tarihinden sonra olmalıdır';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;
    
    // Update dimensions based on location
    const locationOption = locationOptions.find(loc => loc.value === formData.location);
    if (locationOption) {
      formData.dimensions = locationOption.dimensions;
    }
    
    if (editingAd) {
      setAdvertisements(prev => prev.map(ad => 
        ad.id === editingAd.id 
          ? { ...ad, ...formData, updatedAt: new Date().toISOString() }
          : ad
      ));
    } else {
      const newAd: Advertisement = {
        ...formData as Advertisement,
        id: Date.now().toString(),
        viewCount: 0,
        clickCount: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setAdvertisements(prev => [...prev, newAd]);
    }
    setIsModalOpen(false);
    setEditingAd(null);
    setFormData({});
  };

  const handleDelete = (adId: string) => {
    if (confirm('Bu reklamı silmek istediğinizden emin misiniz?')) {
      setAdvertisements(prev => prev.filter(ad => ad.id !== adId));
    }
  };

  const toggleAdStatus = (adId: string) => {
    setAdvertisements(prev => prev.map(ad => 
      ad.id === adId 
        ? { ...ad, isActive: !ad.isActive, updatedAt: new Date().toISOString() }
        : ad
    ));
  };

  const handleLocationChange = (location: string) => {
    const locationOption = locationOptions.find(loc => loc.value === location);
    if (locationOption) {
      setFormData(prev => ({
        ...prev,
        location,
        dimensions: locationOption.dimensions
      }));
    }
  };

  const getLocationLabel = (location: string) => {
    const option = locationOptions.find(loc => loc.value === location);
    return option ? option.label : location;
  };

  const getPageLabels = (pages: string[]) => {
    return pages.map(page => {
      const option = pageOptions.find(p => p.value === page);
      return option ? option.label : page;
    }).join(', ');
  };

  const toggleDropdown = (id: string) => {
    setActiveDropdown(activeDropdown === id ? null : id);
  };

  const renderPreview = () => {
    if (!formData) return null;
    
    let previewWidth = '100%';
    let previewHeight = 'auto';
    
    if (formData.location === 'masthead') {
      previewWidth = previewMode === 'desktop' ? '1200px' : previewMode === 'tablet' ? '768px' : '360px';
      previewHeight = '200px';
    } else {
      previewWidth = '300px';
      previewHeight = '600px';
    }
    
    return (
      <div className="bg-slate-100 p-4 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-slate-800">Önizleme</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPreviewMode('desktop')}
              className={`p-2 rounded-lg transition-colors ${
                previewMode === 'desktop' 
                  ? 'bg-[#F7BA45] text-white' 
                  : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
              }`}
            >
              <Monitor size={16} />
            </button>
            <button
              onClick={() => setPreviewMode('tablet')}
              className={`p-2 rounded-lg transition-colors ${
                previewMode === 'tablet' 
                  ? 'bg-[#F7BA45] text-white' 
                  : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
              }`}
            >
              <Tablet size={16} />
            </button>
            <button
              onClick={() => setPreviewMode('mobile')}
              className={`p-2 rounded-lg transition-colors ${
                previewMode === 'mobile' 
                  ? 'bg-[#F7BA45] text-white' 
                  : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
              }`}
            >
              <Smartphone size={16} />
            </button>
          </div>
        </div>
        
        <div className="flex justify-center">
          <div 
            style={{ 
              width: previewWidth, 
              height: previewHeight,
              maxWidth: '100%',
              overflow: 'hidden'
            }}
            className="border border-slate-300 bg-white"
          >
            {formData.type === 'image' && formData.imageUrl ? (
              <a 
                href={formData.linkUrl || '#'} 
                target="_blank" 
                rel="noopener noreferrer"
                className="block w-full h-full"
              >
                <img 
                  src={formData.imageUrl} 
                  alt={formData.name || 'Reklam önizleme'} 
                  className="w-full h-full object-cover"
                />
              </a>
            ) : formData.type === 'code' && formData.htmlCode ? (
              <div 
                className="w-full h-full"
                dangerouslySetInnerHTML={{ __html: formData.htmlCode }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-slate-50 text-slate-400 text-sm">
                Önizleme için içerik giriniz
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Reklam Yönetimi</h1>
              <p className="text-slate-600">Sayfa bazlı reklam ve banner yönetimi</p>
            </div>
            <div className="flex items-center gap-3">
              <button className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors">
                <Download size={20} />
                <span>Rapor İndir</span>
              </button>
              <button 
                onClick={handleAdd}
                className="flex items-center gap-2 px-4 py-2 bg-[#F7BA45] text-slate-900 rounded-lg hover:bg-[#e5ac3d] transition-colors"
              >
                <Plus size={20} />
                <span>Yeni Reklam</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Search and Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
              <input
                type="text"
                placeholder="Reklam ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
              />
            </div>
            <select
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            >
              <option value="">Tüm Konumlar</option>
              <option value="masthead">Masthead (Üst Banner)</option>
              <option value="sidebar-left">Sol Kenar</option>
              <option value="sidebar-right">Sağ Kenar</option>
            </select>
            <select
              value={filterPage}
              onChange={(e) => setFilterPage(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            >
              <option value="">Tüm Sayfalar</option>
              {pageOptions.map(page => (
                <option key={page.value} value={page.value}>{page.label}</option>
              ))}
            </select>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            >
              <option value="">Tüm Türler</option>
              <option value="image">Görsel</option>
              <option value="code">HTML/JS Kodu</option>
            </select>
          </div>
        </div>

        {/* Ads Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Reklam
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Konum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Sayfalar
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Tarih Aralığı
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    İstatistikler
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Durum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filteredAds.map((ad) => (
                  <tr key={ad.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden">
                          {ad.type === 'image' && ad.imageUrl ? (
                            <img 
                              src={ad.imageUrl} 
                              alt={ad.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Code size={20} className="text-slate-400" />
                          )}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-slate-900">{ad.name}</div>
                          <div className="text-xs text-slate-500">
                            {ad.type === 'image' ? 'Görsel' : 'HTML/JS Kodu'} • {ad.dimensions}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                        ad.location === 'masthead' ? 'bg-blue-100 text-blue-700' :
                        ad.location === 'sidebar-left' ? 'bg-green-100 text-green-700' :
                        'bg-purple-100 text-purple-700'
                      }`}>
                        {getLocationLabel(ad.location)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-slate-600 max-w-[200px] truncate" title={getPageLabels(ad.pages)}>
                        {getPageLabels(ad.pages)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-600">
                        {new Date(ad.startDate).toLocaleDateString('tr-TR')} - {new Date(ad.endDate).toLocaleDateString('tr-TR')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-600">
                        <div>Görüntülenme: {ad.viewCount}</div>
                        <div>Tıklanma: {ad.clickCount}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                        ad.isActive ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {ad.isActive ? 'Aktif' : 'Pasif'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="relative">
                        <button
                          onClick={() => toggleDropdown(ad.id)}
                          className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                        >
                          <MoreHorizontal size={18} className="text-slate-500" />
                        </button>
                        
                        {activeDropdown === ad.id && (
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-10">
                            <button
                              onClick={() => handleEdit(ad)}
                              className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2"
                            >
                              <Edit size={16} className="text-blue-500" />
                              <span>Düzenle</span>
                            </button>
                            <button
                              onClick={() => toggleAdStatus(ad.id)}
                              className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2"
                            >
                              {ad.isActive ? (
                                <>
                                  <EyeOff size={16} className="text-slate-500" />
                                  <span>Pasif Yap</span>
                                </>
                              ) : (
                                <>
                                  <Eye size={16} className="text-green-500" />
                                  <span>Aktif Yap</span>
                                </>
                              )}
                            </button>
                            {ad.type === 'image' ? (
                              <a 
                                href={ad.linkUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2"
                              >
                                <ExternalLink size={16} className="text-slate-500" />
                                <span>Linki Aç</span>
                              </a>
                            ) : (
                              <button
                                onClick={() => {
                                  setFormData(ad);
                                  setIsCodePreviewOpen(true);
                                  setActiveDropdown(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2"
                              >
                                <Code size={16} className="text-slate-500" />
                                <span>Kodu Görüntüle</span>
                              </button>
                            )}
                            <button
                              onClick={() => {
                                handleDelete(ad.id);
                                setActiveDropdown(null);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                            >
                              <Trash2 size={16} />
                              <span>Sil</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredAds.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-slate-500">
                      <div className="flex flex-col items-center">
                        <Image size={40} className="text-slate-300 mb-2" />
                        <p>Hiç reklam bulunamadı</p>
                        <button 
                          onClick={handleAdd}
                          className="mt-4 flex items-center gap-2 px-4 py-2 bg-[#F7BA45] text-slate-900 rounded-lg hover:bg-[#e5ac3d] transition-colors text-sm"
                        >
                          <Plus size={16} />
                          <span>Yeni Reklam Ekle</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Edit/Add Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsModalOpen(false)} />
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl w-full max-w-4xl shadow-2xl max-h-[90vh] overflow-hidden">
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-slate-900">
                    {editingAd ? 'Reklamı Düzenle' : 'Yeni Reklam Ekle'}
                  </h2>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Left Column - Form */}
                  <div className="space-y-6">
                    {/* Basic Info */}
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Reklam Adı
                        </label>
                        <input
                          type="text"
                          value={formData.name || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors ${
                            errors.name ? 'border-red-300 bg-red-50' : 'border-slate-200'
                          }`}
                        />
                        {errors.name && (
                          <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                            <AlertCircle size={14} />
                            {errors.name}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Reklam Türü
                        </label>
                        <select
                          value={formData.type || 'image'}
                          onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as 'image' | 'code' }))}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                        >
                          <option value="image">Görsel</option>
                          <option value="code">HTML/JS Kodu</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Konum
                        </label>
                        <select
                          value={formData.location || 'masthead'}
                          onChange={(e) => handleLocationChange(e.target.value)}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                        >
                          {locationOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label} ({option.dimensions})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Gösterilecek Sayfalar
                        </label>
                        <div className="border border-slate-200 rounded-lg p-3 max-h-40 overflow-y-auto">
                          {pageOptions.map(page => (
                            <label key={page.value} className="flex items-center gap-2 py-1 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.pages?.includes(page.value) || false}
                                onChange={(e) => {
                                  const pages = formData.pages || [];
                                  setFormData(prev => ({
                                    ...prev,
                                    pages: e.target.checked
                                      ? [...pages, page.value]
                                      : pages.filter(p => p !== page.value)
                                  }));
                                }}
                                className="rounded border-slate-300 text-[#F7BA45] focus:ring-[#F7BA45]"
                              />
                              <span className="text-sm text-slate-700">{page.label}</span>
                            </label>
                          ))}
                        </div>
                        {errors.pages && (
                          <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                            <AlertCircle size={14} />
                            {errors.pages}
                          </p>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Başlangıç Tarihi
                          </label>
                          <input
                            type="date"
                            value={formData.startDate || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors ${
                              errors.startDate ? 'border-red-300 bg-red-50' : 'border-slate-200'
                            }`}
                          />
                          {errors.startDate && (
                            <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                              <AlertCircle size={14} />
                              {errors.startDate}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Bitiş Tarihi
                          </label>
                          <input
                            type="date"
                            value={formData.endDate || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors ${
                              errors.endDate ? 'border-red-300 bg-red-50' : 'border-slate-200'
                            }`}
                          />
                          {errors.endDate && (
                            <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                              <AlertCircle size={14} />
                              {errors.endDate}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="isActive"
                          checked={formData.isActive || false}
                          onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                          className="rounded border-slate-300 text-[#F7BA45] focus:ring-[#F7BA45]"
                        />
                        <label htmlFor="isActive" className="text-sm text-slate-700">Aktif</label>
                      </div>
                    </div>

                    {/* Type Specific Fields */}
                    {formData.type === 'image' ? (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Görsel URL
                          </label>
                          <input
                            type="url"
                            value={formData.imageUrl || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, imageUrl: e.target.value }))}
                            placeholder="https://example.com/image.jpg"
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors ${
                              errors.imageUrl ? 'border-red-300 bg-red-50' : 'border-slate-200'
                            }`}
                          />
                          {errors.imageUrl && (
                            <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                              <AlertCircle size={14} />
                              {errors.imageUrl}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Yönlendirme Linki
                          </label>
                          <input
                            type="url"
                            value={formData.linkUrl || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, linkUrl: e.target.value }))}
                            placeholder="https://example.com/landing-page"
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors ${
                              errors.linkUrl ? 'border-red-300 bg-red-50' : 'border-slate-200'
                            }`}
                          />
                          {errors.linkUrl && (
                            <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                              <AlertCircle size={14} />
                              {errors.linkUrl}
                            </p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            HTML/JS Kodu
                          </label>
                          <textarea
                            value={formData.htmlCode || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, htmlCode: e.target.value }))}
                            rows={8}
                            placeholder="<div>Reklam içeriği</div>"
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors font-mono text-sm ${
                              errors.htmlCode ? 'border-red-300 bg-red-50' : 'border-slate-200'
                            }`}
                          />
                          {errors.htmlCode && (
                            <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                              <AlertCircle size={14} />
                              {errors.htmlCode}
                            </p>
                          )}
                        </div>
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <div className="flex items-start gap-2">
                            <Info size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-amber-700">
                              <p>HTML/JS kodunuzun güvenli olduğundan emin olun. Kod, sayfa içinde iframe olmadan doğrudan çalıştırılacaktır.</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right Column - Preview */}
                  <div>
                    {renderPreview()}
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-slate-200">
                <div className="flex items-center justify-end gap-3">
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 text-slate-600 hover:text-slate-900 transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleSave}
                    className="flex items-center gap-2 px-4 py-2 bg-[#F7BA45] text-slate-900 rounded-lg hover:bg-[#e5ac3d] transition-colors"
                  >
                    <Save size={20} />
                    <span>Kaydet</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Code Preview Modal */}
      {isCodePreviewOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsCodePreviewOpen(false)} />
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-hidden">
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-slate-900">
                    HTML/JS Kodu Önizleme
                  </h2>
                  <button
                    onClick={() => setIsCodePreviewOpen(false)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                <div className="bg-slate-100 p-4 rounded-lg mb-6">
                  <div className="bg-white border border-slate-200 rounded-lg p-4 overflow-hidden" style={{ height: '300px' }}>
                    {formData.htmlCode ? (
                      <div dangerouslySetInnerHTML={{ __html: formData.htmlCode }} />
                    ) : (
                      <div className="flex items-center justify-center h-full text-slate-400">
                        Kod önizlemesi bulunamadı
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-slate-800 text-slate-200 p-4 rounded-lg overflow-x-auto">
                  <pre className="text-sm font-mono whitespace-pre-wrap">
                    {formData.htmlCode || '// Kod bulunamadı'}
                  </pre>
                </div>
              </div>

              <div className="p-6 border-t border-slate-200">
                <div className="flex items-center justify-end">
                  <button
                    onClick={() => setIsCodePreviewOpen(false)}
                    className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                  >
                    Kapat
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvertisementManagement;